// import React, { useState, useEffect, useRef } from "react";
// import { useParams, useNavigate } from "react-router-dom";
// import { useAuth } from "@/contexts/AuthContext";
// import { Card } from "@/components/ui/card";
// import { useToast } from "@/hooks/use-toast";
// import { examCandidateAPI } from "@/services/api";
// import { AlertTriangle } from "lucide-react";

// import {
//   CameraPermissionDialog,
//   WarningDialog,
// } from "./exam-utils/DialogModals";
// import SubmissionWarningDialog from "./exam-utils/SubmissionWarningDialog";
// import ExamTimer from "./exam-utils/ExamTimer";
// import FaceDetectionHandler from "./exam-utils/FaceDetectionHandler";
// import QuestionNavigator from "./exam-utils/QuestionNavigator";
// import QuestionDisplay from "./exam-utils/QuestionDisplay";
// import {
//   sendProctoringData,
//   handleVisibilityChange,
// } from "./exam-utils/ExamUtils";

// const ExamSession = ({ sessionData: propSessionData }) => {
//   const { sessionId } = useParams();
//   const { user, logout } = useAuth();
//   const navigate = useNavigate();
//   const { toast } = useToast();
//   const lastTabActiveTimeRef = useRef(0);
//   const tabSwitchCountRef = useRef(0);
//   const timeWarningsShownRef = useRef(new Set());
//   const submissionInProgressRef = useRef(false);
//   const redirectTimeoutRef = useRef(null);

//   // Exam state
//   const [loading, setLoading] = useState(true);
//   const [submitLoading, setSubmitLoading] = useState(false);
//   const [autoSubmissionLoading, setAutoSubmissionLoading] = useState(false); // New loading state for auto submissions
//   const [exam, setExam] = useState(null);
//   const [questions, setQuestions] = useState([]);
//   const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
//   const [answers, setAnswers] = useState({});

//   // Face detection state
//   const [faceDetected, setFaceDetected] = useState(false);
//   const [faceDetectionEnabled, setFaceDetectionEnabled] = useState(true);

//   // Dialog states
//   const [showCameraPermissionDialog, setShowCameraPermissionDialog] =
//     useState(false);
//   const [showFaceNotDetectedDialog, setShowFaceNotDetectedDialog] =
//     useState(false);
//   const [showWarning, setShowWarning] = useState(false);
//   const [warningMessage, setWarningMessage] = useState("");
//   const [warningCount, setWarningCount] = useState(0);
//   const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
//   const [unansweredQuestions, setUnansweredQuestions] = useState([]);

//   // Time management
//   const [startTime, setStartTime] = useState(null);
//   const [totalTime, setTotalTime] = useState(null);
//   const [timeRemaining, setTimeRemaining] = useState(null);

//   // Cleanup timeout on unmount
//   useEffect(() => {
//     return () => {
//       if (redirectTimeoutRef.current) {
//         clearTimeout(redirectTimeoutRef.current);
//       }
//     };
//   }, []);

//   // Load exam session data
//   useEffect(() => {
//     const fetchExamSession = async () => {
//       try {
//         let sessionInfo;

//         if (propSessionData) {
//           sessionInfo = propSessionData;
//         } else if (sessionId) {
//           const response = await examCandidateAPI.getExamSession(sessionId);
//           sessionInfo = response.data;
//         } else {
//           throw new Error("No session ID provided");
//         }


//         if (!sessionInfo.exam || !sessionInfo.questions) {
//           throw new Error("Invalid session data structure");
//         }

//         setExam(sessionInfo.exam);
//         setQuestions(sessionInfo.questions);
//         setCurrentQuestionIndex(sessionInfo.currentQuestionIndex || 0);
//         setWarningCount(sessionInfo.warningCount || 0);
//         setStartTime(new Date(sessionInfo.startTime).getTime());
//         setTotalTime(sessionInfo.exam.totalTime * 60);
//         setTimeRemaining(sessionInfo.timeRemaining);

//         // Initialize answers from saved state
//         const savedAnswers = {};
//         if (sessionInfo.answers?.length > 0) {
//           sessionInfo.answers.forEach((answer) => {
//             savedAnswers[answer.questionId] = answer.answer;
//           });
//         }
//         setAnswers(savedAnswers);

//         if (sessionInfo.exam.proctoring?.enabled) {
//           setShowCameraPermissionDialog(true);
//         } else {
//           setFaceDetectionEnabled(false);
//           setLoading(false);
//         }
//       } catch (error) {
//         console.error("Error fetching exam session:", error);
//         toast({
//           title: "Error",
//           description: "Failed to load exam session",
//           variant: "destructive",
//         });
//         navigate("/candidate");
//       }
//     };

//     fetchExamSession();
//   }, [sessionId, navigate, toast, propSessionData]);

//   // Time warning system
//   useEffect(() => {
//     if (!timeRemaining || timeRemaining <= 0) return;

//     const checkTimeWarnings = () => {
//       const remainingPercentage = (timeRemaining / totalTime) * 100;

//       // 20% warning
//       if (
//         remainingPercentage <= 20 &&
//         remainingPercentage > 10 &&
//         !timeWarningsShownRef.current.has("20")
//       ) {
//         timeWarningsShownRef.current.add("20");
//         toast({
//           title: "Time Warning",
//           description: "20% of exam time remaining",
//           variant: "destructive",
//           duration: 10000,
//         });

//         sendProctoringData(sessionId, examCandidateAPI, {
//           type: "timeWarning",
//           timestamp: new Date().toISOString(),
//           details: "20% time remaining warning shown",
//         });
//       }

//       // 10% warning
//       if (
//         remainingPercentage <= 10 &&
//         remainingPercentage > 5 &&
//         !timeWarningsShownRef.current.has("10")
//       ) {
//         timeWarningsShownRef.current.add("10");
//         toast({
//           title: "Time Warning",
//           description:
//             "10% of exam time remaining - Please complete your answers soon",
//           variant: "destructive",
//           duration: 10000,
//         });

//         sendProctoringData(sessionId, examCandidateAPI, {
//           type: "timeWarning",
//           timestamp: new Date().toISOString(),
//           details: "10% time remaining warning shown",
//         });
//       }

//       // 5% warning
//       if (
//         remainingPercentage <= 5 &&
//         remainingPercentage > 0 &&
//         !timeWarningsShownRef.current.has("5")
//       ) {
//         timeWarningsShownRef.current.add("5");
//         toast({
//           title: "Final Warning",
//           description:
//             "Only 5% of exam time remaining - Exam will auto-submit soon!",
//           variant: "destructive",
//           duration: 15000,
//         });

//         sendProctoringData(sessionId, examCandidateAPI, {
//           type: "timeWarning",
//           timestamp: new Date().toISOString(),
//           details: "5% time remaining - final warning shown",
//         });
//       }
//     };

//     checkTimeWarnings();
//   }, [timeRemaining, totalTime, sessionId, toast]);

//   // Add visibility change detection
//   useEffect(() => {
//     const visibilityChangeHandler = () => {
//       handleVisibilityChange(
//         document,
//         lastTabActiveTimeRef,
//         tabSwitchCountRef,
//         sessionId,
//         exam,
//         examCandidateAPI,
//         (warningMsg, isDisqualified) => {
//           if (isDisqualified) {
//             handleDisqualification("Exam rules violated - tab switching");
//           } else {
//             setWarningMessage(warningMsg);
//             setShowWarning(true);
//             setWarningCount((prev) => prev + 1);
//           }
//         }
//       );
//     };

//     if (exam?.proctoring?.enabled && exam?.proctoring?.trackTabSwitching) {
//       document.addEventListener("visibilitychange", visibilityChangeHandler);

//       sendProctoringData(sessionId, examCandidateAPI, {
//         type: "proctoringStatus",
//         timestamp: new Date().toISOString(),
//         details: "Tab switching monitoring enabled",
//       });
//     }

//     return () => {
//       if (exam?.proctoring?.enabled && exam?.proctoring?.trackTabSwitching) {
//         document.removeEventListener(
//           "visibilitychange",
//           visibilityChangeHandler
//         );
//       }
//     };
//   }, [exam, sessionId]);

//   // Handle warnings and disqualification
//   useEffect(() => {
//     if (exam?.warningsAllowed > 0 && warningCount >= exam.warningsAllowed) {
//       handleDisqualification("Exam rules violated - too many warnings");
//     }
//   }, [warningCount, exam]);

//   const handleAnswerChange = (value) => {
//     if (!questions[currentQuestionIndex]) return;

//     if (faceDetectionEnabled && !faceDetected) {
//       setShowFaceNotDetectedDialog(true);
//       toast({
//         title: "Face Not Detected",
//         description: "Please ensure your face is visible to continue",
//         variant: "destructive",
//       });
//       return;
//     }

//     const questionId = questions[currentQuestionIndex]._id;
//     setAnswers((prev) => ({
//       ...prev,
//       [questionId]: value,
//     }));

//     // Auto-save the answer immediately
//     saveAnswer(questionId, value);
//   };

//   const saveAnswer = async (questionId, answer) => {
//     try {
//       await examCandidateAPI.saveAnswer(sessionId, {
//         questionId,
//         answer,
//       });

//       await sendProctoringData(sessionId, examCandidateAPI, {
//         type: "answer_saved",
//         timestamp: new Date().toISOString(),
//         details: "Answer saved",
//       });
//     } catch (error) {
//       console.error("Error saving answer:", error);
//       toast({
//         title: "Error",
//         description: "Failed to save your answer",
//         variant: "destructive",
//       });
//     }
//   };

//   const navigateToQuestion = async (index) => {
//     if (index < 0 || index >= questions.length) return;

//     if (faceDetectionEnabled && !faceDetected) {
//       setShowFaceNotDetectedDialog(true);
//       return;
//     }

//     try {
//       await examCandidateAPI.updateProgress(sessionId, {
//         currentQuestionIndex: index,
//       });
//       setCurrentQuestionIndex(index);
//     } catch (error) {
//       console.error("Error updating progress:", error);
//       toast({
//         title: "Error",
//         description: "Failed to update progress",
//         variant: "destructive",
//       });
//     }
//   };

//   const handleTimeUp = () => {
//     // Prevent multiple submissions
//     if (submissionInProgressRef.current) {
//       return;
//     }

//     submissionInProgressRef.current = true;
//     setAutoSubmissionLoading(true);

//     toast({
//       title: "Time's Up!",
//       description: "Your exam time has finished. Auto-submitting answers...",
//       duration: 5000,
//     });

//     sendProctoringData(sessionId, examCandidateAPI, {
//       type: "timeExpired",
//       timestamp: new Date().toISOString(),
//       details: "Exam time expired - auto-submitting",
//     });

//     // Force submit immediately without delay to prevent multiple calls
//     handleSubmitExam(true, "timeout");
//   };

//   const checkUnansweredQuestions = () => {
//     const unanswered = [];
//     questions.forEach((question, index) => {
//       const questionId = question._id;
//       const answer = answers[questionId];

//       // Check if answer is empty, null, undefined, or empty array
//       if (
//         !answer ||
//         answer === "" ||
//         answer === null ||
//         answer === undefined ||
//         (Array.isArray(answer) && answer.length === 0)
//       ) {
//         unanswered.push(index + 1); // Question numbers start from 1
//       }
//     });


//     return unanswered;
//   };

//   const handleSubmitExam = async (forceSubmit = false, reason = null) => {
//     // Prevent multiple submissions
//     if (submissionInProgressRef.current) {
//       return;
//     }


//     // Only check face detection for manual submissions
//     if (!forceSubmit && faceDetectionEnabled && !faceDetected) {
//       setShowFaceNotDetectedDialog(true);
//       return;
//     }

//     // Only check for unanswered questions during manual submission
//     if (!forceSubmit) {
//       const unanswered = checkUnansweredQuestions();

//       if (unanswered.length > 0) {
//         setUnansweredQuestions(unanswered);
//         setShowSubmissionWarning(true);
//         return;
//       }
//     } else {
//       console.log(
//         "Force submit - skipping unanswered questions check. Reason:",
//         reason
//       );
//     }

//     try {
//       submissionInProgressRef.current = true;

//       // Set appropriate loading state
//       if (forceSubmit) {
//         setAutoSubmissionLoading(true);
//       } else {
//         setSubmitLoading(true);
//       }


//       const result = await examCandidateAPI.completeExam(sessionId);

//       toast({
//         title: "Exam Submitted",
//         description: "Your exam has been submitted successfully!",
//         duration: 3000,
//       });

//       // Navigate to completion page with result data
//       const navigationData = {
//         examName: exam?.name,
//         examCompleted: true,
//         result: result.data?.result,
//         submissionType: reason || "manual",
//       };


//       // Clear any existing timeout
//       if (redirectTimeoutRef.current) {
//         clearTimeout(redirectTimeoutRef.current);
//       }

//       // For auto submissions, add a slight delay to ensure user sees the success message
//       if (forceSubmit) {
//         redirectTimeoutRef.current = setTimeout(() => {
//           navigate("/candidate/exam-complete", {
//             state: navigationData,
//             replace: true, // Use replace to prevent going back to exam
//           });
//         }, 2000);
//       } else {
//         // For manual submissions, navigate immediately
//         navigate("/candidate/exam-complete", {
//           state: navigationData,
//           replace: true,
//         });
//       }
//     } catch (error) {
//       console.error("Error submitting exam:", error);
//       submissionInProgressRef.current = false;
//       setAutoSubmissionLoading(false);
//       setSubmitLoading(false);

//       toast({
//         title: "Error",
//         description: "Failed to submit exam. Please try again.",
//         variant: "destructive",
//       });
//     }
//   };

//   const handleDisqualification = async (reason) => {
//     // Prevent multiple disqualifications
//     if (submissionInProgressRef.current) {
//       return;
//     }

//     submissionInProgressRef.current = true;
//     setAutoSubmissionLoading(true);

//     try {
//       await sendProctoringData(sessionId, examCandidateAPI, {
//         type: "disqualified",
//         timestamp: new Date().toISOString(),
//         details: reason,
//       });

//       toast({
//         title: "Disqualified",
//         description: reason,
//         variant: "destructive",
//         duration: 5000,
//       });

//       // Force submit immediately
//       await handleSubmitExam(true, "disqualification");

//       // Additional timeout for logout after disqualification
//       redirectTimeoutRef.current = setTimeout(() => {
//         logout();
//         navigate("/candidate-login", { replace: true });
//       }, 5000);
//     } catch (error) {
//       console.error("Error handling disqualification:", error);
//       submissionInProgressRef.current = false;
//       setAutoSubmissionLoading(false);
//     }
//   };

//   // Show loading screen for auto submissions
//   if (autoSubmissionLoading) {
//     return (
//       <div className="flex justify-center items-center h-screen bg-gray-50">
//         <div className="flex flex-col items-center p-8 bg-white rounded-lg shadow-lg">
//           <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mb-4"></div>
//           <h2 className="text-xl font-semibold mb-2">Submitting Exam...</h2>
//           <p className="text-gray-600 text-center">
//             Please wait while we process your submission.
//             <br />
//             Do not close this window.
//           </p>
//         </div>
//       </div>
//     );
//   }
//   if (loading) {
//     return (
//       <div className="flex justify-center items-center h-screen">
//         <CameraPermissionDialog
//           open={showCameraPermissionDialog}
//           onPermissionGranted={() => {
//             setShowCameraPermissionDialog(false);
//             setLoading(false);
//           }}
//           onPermissionDenied={() => {
//             setFaceDetectionEnabled(false);
//             setShowCameraPermissionDialog(false);
//             setLoading(false);
//           }}
//           required={exam?.proctoring?.required}
//         />
//         <div className="flex flex-col items-center">
//           <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
//           <p className="mt-4 text-gray-600">Loading exam...</p>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="container mx-auto px-4 py-8">
//       <FaceDetectionHandler
//         sessionId={sessionId}
//         exam={exam}
//         examCandidateAPI={examCandidateAPI}
//         onFaceDetectionChange={setFaceDetected}
//         faceDetectionEnabled={faceDetectionEnabled}
//       />

//       {/* Warning count badge */}
//       {warningCount > 0 && (
//         <div className="fixed top-16 right-4 bg-amber-500 text-white px-3 py-1 rounded-full z-50 flex items-center">
//           <AlertTriangle className="h-4 w-4 mr-1" />
//           <span>
//             Warnings: {warningCount}/{exam?.warningsAllowed || "∞"}
//           </span>
//         </div>
//       )}

//       {/* Exam header */}
//       <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
//         <div>
//           <h1 className="text-2xl font-bold">{exam?.name}</h1>
//           <p className="text-gray-500">
//             Question {currentQuestionIndex + 1} of {questions.length}
//           </p>
//         </div>

//         <ExamTimer
//           startTime={startTime}
//           totalTimeSeconds={totalTime}
//           timeRemaining={timeRemaining}
//           onTimeUp={handleTimeUp}
//           onTimeUpdate={setTimeRemaining}
//         />
//       </div>

//       {/* Main content */}
//       <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
//         {/* Question navigation sidebar */}
//         <div className="hidden md:block">
//           <QuestionNavigator
//             questions={questions}
//             currentQuestionIndex={currentQuestionIndex}
//             answers={answers}
//             onNavigate={navigateToQuestion}
//             disabled={faceDetectionEnabled && !faceDetected}
//           />
//         </div>

//         {/* Question display */}
//         <div className="md:col-span-3">
//           <QuestionDisplay
//             currentQuestion={questions[currentQuestionIndex]}
//             currentQuestionIndex={currentQuestionIndex}
//             totalQuestions={questions.length}
//             answer={answers[questions[currentQuestionIndex]?._id]}
//             onAnswerChange={handleAnswerChange}
//             onNavigate={navigateToQuestion}
//             onSubmit={() => {
//               handleSubmitExam(false, "manual_confirm");
//             }}
//             faceDetectionEnabled={faceDetectionEnabled}
//             faceDetected={faceDetected}
//             submitLoading={submitLoading}
//             answeredQuestions={Object.keys(answers)}
//           />
//         </div>
//       </div>

//       {/* Dialogs */}
//       <WarningDialog
//         open={showWarning}
//         onAcknowledge={() => setShowWarning(false)}
//         warningMessage={warningMessage}
//         warningCount={warningCount}
//         warningsAllowed={exam?.warningsAllowed}
//       />

//       <SubmissionWarningDialog
//         open={showSubmissionWarning}
//         onConfirm={() => {
//           setShowSubmissionWarning(false);
//           handleSubmitExam(true, "manual_confirm");
//         }}
//         onCancel={() => setShowSubmissionWarning(false)}
//         unansweredQuestions={unansweredQuestions}
//       />
//     </div>
//   );
// };

// export default ExamSession;

import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { examCandidateAPI } from "@/services/api";
import { AlertTriangle } from "lucide-react";

import {
  CameraPermissionDialog,
  WarningDialog,
  ScreenCapturePermissionDialog,
} from "./exam-utils/DialogModals";
import SubmissionWarningDialog from "./exam-utils/SubmissionWarningDialog";
import ExamTimer from "./exam-utils/ExamTimer";
import FaceDetectionHandler from "./exam-utils/FaceDetectionHandler";
import ScreenCaptureHandler from "./exam-utils/ScreenCaptureHandler";
import QuestionNavigator from "./exam-utils/QuestionNavigator";
import QuestionDisplay from "./exam-utils/QuestionDisplay";
import {
  sendProctoringData,
  handleVisibilityChange,
} from "./exam-utils/ExamUtils";

const ExamSession = ({ sessionData: propSessionData }) => {
  const { sessionId } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const lastTabActiveTimeRef = useRef(0);
  const tabSwitchCountRef = useRef(0);
  const timeWarningsShownRef = useRef(new Set());
  const submissionInProgressRef = useRef(false);
  const redirectTimeoutRef = useRef(null);
  const timeUpHandledRef = useRef(false); // Track if time up has been handled

  // Exam state
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [autoSubmissionLoading, setAutoSubmissionLoading] = useState(false);
  const [exam, setExam] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  // Face detection state
  const [faceDetected, setFaceDetected] = useState(false);
  const [faceDetectionEnabled, setFaceDetectionEnabled] = useState(false);
  const [screenCaptureEnabled, setScreenCaptureEnabled] = useState(false);

  // Dialog states
  const [showCameraPermissionDialog, setShowCameraPermissionDialog] =
    useState(false);
  const [showFaceNotDetectedDialog, setShowFaceNotDetectedDialog] =
    useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningMessage, setWarningMessage] = useState("");
  const [warningCount, setWarningCount] = useState(0);
  const [showSubmissionWarning, setShowSubmissionWarning] = useState(false);
  const [unansweredQuestions, setUnansweredQuestions] = useState([]);

  // Time management
  const [startTime, setStartTime] = useState(null);
  const [totalTime, setTotalTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  // Load exam session data
  useEffect(() => {
    const fetchExamSession = async () => {
      try {
        let sessionInfo;

        if (propSessionData) {
          sessionInfo = propSessionData;
        } else if (sessionId) {
          const response = await examCandidateAPI.getExamSession(sessionId);
          sessionInfo = response.data;
        } else {
          throw new Error("No session ID provided");
        }


        if (!sessionInfo.exam || !sessionInfo.questions) {
          throw new Error("Invalid session data structure");
        }

        setExam(sessionInfo.exam);
        setQuestions(sessionInfo.questions);
        setCurrentQuestionIndex(sessionInfo.currentQuestionIndex || 0);
        setWarningCount(sessionInfo.warningCount || 0);
        setStartTime(new Date(sessionInfo.startTime).getTime());
        setTotalTime(sessionInfo.exam.totalTime * 60);
        setTimeRemaining(sessionInfo.timeRemaining);

        // Initialize answers from saved state
        const savedAnswers = {};
        if (sessionInfo.answers?.length > 0) {
          sessionInfo.answers.forEach((answer) => {
            savedAnswers[answer.questionId] = answer.answer;
          });
        }
        setAnswers(savedAnswers);

        // Configure face detection based on exam proctoring settings
        const proctoringEnabled = sessionInfo.exam.proctoring?.enabled || false;
        const faceDetectionRequired = sessionInfo.exam.proctoring?.faceDetection || false;
        const captureImagesEnabled = sessionInfo.exam.proctoring?.captureImages || false;
        
        
        if (proctoringEnabled && faceDetectionRequired) {
          setFaceDetectionEnabled(true);
          setShowCameraPermissionDialog(true);
        } else {
          setFaceDetectionEnabled(false);
          setLoading(false);
        }

        // Enable screen capture if configured
        if (proctoringEnabled && captureImagesEnabled) {
          setScreenCaptureEnabled(true);
        } else {
          console.log('📸 Screen capture not enabled - Proctoring:', proctoringEnabled, 'CaptureImages:', captureImagesEnabled);
        }
      } catch (error) {
        console.error("Error fetching exam session:", error);
        toast({
          title: "Error",
          description: "Failed to load exam session",
          variant: "destructive",
        });
        navigate("/candidate");
      }
    };

    fetchExamSession();
  }, [sessionId, navigate, toast, propSessionData]);

  // Time warning system
  useEffect(() => {
    if (!timeRemaining || timeRemaining <= 0) return;

    const checkTimeWarnings = () => {
      const remainingPercentage = (timeRemaining / totalTime) * 100;

      // 20% warning
      if (
        remainingPercentage <= 20 &&
        remainingPercentage > 10 &&
        !timeWarningsShownRef.current.has("20")
      ) {
        timeWarningsShownRef.current.add("20");
        toast({
          title: "Time Warning",
          description: "20% of exam time remaining",
          variant: "destructive",
          duration: 10000,
        });

        sendProctoringData(sessionId, examCandidateAPI, {
          type: "timeWarning",
          timestamp: new Date().toISOString(),
          details: "20% time remaining warning shown",
        });
      }

      // 10% warning
      if (
        remainingPercentage <= 10 &&
        remainingPercentage > 5 &&
        !timeWarningsShownRef.current.has("10")
      ) {
        timeWarningsShownRef.current.add("10");
        toast({
          title: "Time Warning",
          description:
            "10% of exam time remaining - Please complete your answers soon",
          variant: "destructive",
          duration: 10000,
        });

        sendProctoringData(sessionId, examCandidateAPI, {
          type: "timeWarning",
          timestamp: new Date().toISOString(),
          details: "10% time remaining warning shown",
        });
      }

      // 5% warning
      if (
        remainingPercentage <= 5 &&
        remainingPercentage > 0 &&
        !timeWarningsShownRef.current.has("5")
      ) {
        timeWarningsShownRef.current.add("5");
        toast({
          title: "Final Warning",
          description:
            "Only 5% of exam time remaining - Exam will auto-submit soon!",
          variant: "destructive",
          duration: 15000,
        });

        sendProctoringData(sessionId, examCandidateAPI, {
          type: "timeWarning",
          timestamp: new Date().toISOString(),
          details: "5% time remaining - final warning shown",
        });
      }
    };

    checkTimeWarnings();
  }, [timeRemaining, totalTime, sessionId, toast]);

  // Add visibility change detection
  useEffect(() => {
    const visibilityChangeHandler = () => {
      handleVisibilityChange(
        document,
        lastTabActiveTimeRef,
        tabSwitchCountRef,
        sessionId,
        exam,
        examCandidateAPI,
        (warningMsg, isDisqualified) => {
          if (isDisqualified) {
            handleDisqualification("Exam rules violated - tab switching");
          } else {
            setWarningMessage(warningMsg);
            setShowWarning(true);
            setWarningCount((prev) => prev + 1);
          }
        }
      );
    };

    if (exam?.proctoring?.enabled && exam?.proctoring?.trackTabSwitching) {
      document.addEventListener("visibilitychange", visibilityChangeHandler);

      sendProctoringData(sessionId, examCandidateAPI, {
        type: "proctoringStatus",
        timestamp: new Date().toISOString(),
        details: "Tab switching monitoring enabled",
      });
    }

    return () => {
      if (exam?.proctoring?.enabled && exam?.proctoring?.trackTabSwitching) {
        document.removeEventListener(
          "visibilitychange",
          visibilityChangeHandler
        );
      }
    };
  }, [exam, sessionId]);

  // Handle warnings and disqualification
  useEffect(() => {
    if (exam?.warningsAllowed > 0 && warningCount >= exam.warningsAllowed) {
      handleDisqualification("Exam rules violated - too many warnings");
    }
  }, [warningCount, exam]);

  const handleAnswerChange = (value) => {
    if (!questions[currentQuestionIndex]) return;

    if (faceDetectionEnabled && !faceDetected) {
      setShowFaceNotDetectedDialog(true);
      toast({
        title: "Face Not Detected",
        description: "Please ensure your face is visible to continue",
        variant: "destructive",
      });
      return;
    }

    const questionId = questions[currentQuestionIndex]._id;
    setAnswers((prev) => ({
      ...prev,
      [questionId]: value,
    }));

    // Auto-save the answer immediately
    saveAnswer(questionId, value);
  };

  const saveAnswer = async (questionId, answer) => {
    try {
      await examCandidateAPI.saveAnswer(sessionId, {
        questionId,
        answer,
      });

      await sendProctoringData(sessionId, examCandidateAPI, {
        type: "answer_saved",
        timestamp: new Date().toISOString(),
        details: "Answer saved",
      });
    } catch (error) {
      console.error("Error saving answer:", error);
      toast({
        title: "Error",
        description: "Failed to save your answer",
        variant: "destructive",
      });
    }
  };

  const navigateToQuestion = async (index) => {
    if (index < 0 || index >= questions.length) return;

    if (faceDetectionEnabled && !faceDetected) {
      setShowFaceNotDetectedDialog(true);
      return;
    }

    try {
      await examCandidateAPI.updateProgress(sessionId, {
        currentQuestionIndex: index,
      });
      setCurrentQuestionIndex(index);
    } catch (error) {
      console.error("Error updating progress:", error);
      toast({
        title: "Error",
        description: "Failed to update progress",
        variant: "destructive",
      });
    }
  };

  const handleTimeUp = () => {
    // Ensure we only handle time up once
    if (timeUpHandledRef.current) {
      return;
    }
    timeUpHandledRef.current = true;

    // Prevent multiple submissions
    if (submissionInProgressRef.current) {
      return;
    }

    submissionInProgressRef.current = true;
    setAutoSubmissionLoading(true);

    toast({
      title: "Time's Up!",
      description: "Your exam time has finished. Auto-submitting answers...",
      duration: 5000,
    });

    sendProctoringData(sessionId, examCandidateAPI, {
      type: "timeExpired",
      timestamp: new Date().toISOString(),
      details: "Exam time expired - auto-submitting",
    });

    // Force submit immediately without delay
    handleSubmitExam(true, "timeout");
  };

  const checkUnansweredQuestions = () => {
    const unanswered = [];
    questions.forEach((question, index) => {
      const questionId = question._id;
      const answer = answers[questionId];

      if (
        !answer ||
        answer === "" ||
        answer === null ||
        answer === undefined ||
        (Array.isArray(answer) && answer.length === 0)
      ) {
        unanswered.push(index + 1);
      }
    });

    return unanswered;
  };

  const handleSubmitExam = async (forceSubmit = false, reason = null) => {
    // Prevent multiple submissions
    if (submissionInProgressRef.current && !forceSubmit) {
      return;
    }


    // Only check face detection for manual submissions
    if (!forceSubmit && faceDetectionEnabled && !faceDetected) {
      setShowFaceNotDetectedDialog(true);
      return;
    }

    // Only check for unanswered questions during manual submission
    if (!forceSubmit) {
      const unanswered = checkUnansweredQuestions();
      if (unanswered.length > 0) {
        setUnansweredQuestions(unanswered);
        setShowSubmissionWarning(true);
        return;
      }
    }

    try {
      submissionInProgressRef.current = true;

      // Set appropriate loading state
      if (forceSubmit) {
        setAutoSubmissionLoading(true);
      } else {
        setSubmitLoading(true);
      }


      const result = await examCandidateAPI.completeExam(sessionId);

      toast({
        title: "Exam Submitted",
        description: "Your exam has been submitted successfully!",
        duration: 3000,
      });

      // Navigate to completion page with result data
      const navigationData = {
        examName: exam?.name,
        examCompleted: true,
        result: result.data?.result,
        submissionType: reason || "manual",
      };

      // Clear any existing timeout
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }

      // For auto submissions, add a slight delay to ensure user sees the success message
      redirectTimeoutRef.current = setTimeout(() => {
        navigate("/candidate/exam-complete", {
          state: navigationData,
          replace: true,
        });
      }, forceSubmit ? 2000 : 0); // 2s delay for auto-submit, immediate for manual
    } catch (error) {
      console.error("Error submitting exam:", error);
      submissionInProgressRef.current = false;
      setAutoSubmissionLoading(false);
      setSubmitLoading(false);

      toast({
        title: "Error",
        description: "Failed to submit exam. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleDisqualification = async (reason) => {
    if (submissionInProgressRef.current) {
      return;
    }

    submissionInProgressRef.current = true;
    setAutoSubmissionLoading(true);

    try {
      await sendProctoringData(sessionId, examCandidateAPI, {
        type: "disqualified",
        timestamp: new Date().toISOString(),
        details: reason,
      });

      toast({
        title: "Disqualified",
        description: reason,
        variant: "destructive",
        duration: 5000,
      });

      await handleSubmitExam(true, "disqualification");

      redirectTimeoutRef.current = setTimeout(() => {
        logout();
        navigate("/candidate-login", { replace: true });
      }, 5000);
    } catch (error) {
      console.error("Error handling disqualification:", error);
      submissionInProgressRef.current = false;
      setAutoSubmissionLoading(false);
    }
  };

  // Show loading screen for auto submissions
  if (autoSubmissionLoading) {
    return (
      <div className="flex justify-center items-center h-screen bg-gray-50">
        <div className="flex flex-col items-center p-8 bg-white rounded-lg shadow-lg">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary mb-4"></div>
          <h2 className="text-xl font-semibold mb-2">Submitting Exam...</h2>
          <p className="text-gray-600 text-center">
            Please wait while we process your submission.
            <br />
            Do not close this window.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <CameraPermissionDialog
          open={showCameraPermissionDialog}
          onPermissionGranted={() => {
            setShowCameraPermissionDialog(false);
            setLoading(false);
          }}
          onPermissionDenied={() => {
            setFaceDetectionEnabled(false);
            setShowCameraPermissionDialog(false);
            setLoading(false);
          }}
          required={exam?.proctoring?.required}
        />
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading exam...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <FaceDetectionHandler
        sessionId={sessionId}
        exam={exam}
        examCandidateAPI={examCandidateAPI}
        onFaceDetectionChange={setFaceDetected}
        faceDetectionEnabled={faceDetectionEnabled}
      />

      <ScreenCaptureHandler
        sessionId={sessionId}
        exam={exam}
        examCandidateAPI={examCandidateAPI}
        captureEnabled={screenCaptureEnabled}
      />

      {/* Warning count badge */}
      {warningCount > 0 && (
        <div className="fixed top-16 right-4 bg-amber-500 text-white px-3 py-1 rounded-full z-50 flex items-center">
          <AlertTriangle className="h-4 w-4 mr-1" />
          <span>
            Warnings: {warningCount}/{exam?.warningsAllowed || "∞"}
          </span>
        </div>
      )}

      {/* Exam header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold">{exam?.name}</h1>
          <p className="text-gray-500">
            Question {currentQuestionIndex + 1} of {questions.length}
          </p>
        </div>

        <ExamTimer
          startTime={startTime}
          totalTimeSeconds={totalTime}
          timeRemaining={timeRemaining}
          onTimeUp={handleTimeUp}
          onTimeUpdate={setTimeRemaining}
        />
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {/* Question navigation sidebar */}
        <div className="hidden md:block">
          <QuestionNavigator
            questions={questions}
            currentQuestionIndex={currentQuestionIndex}
            answers={answers}
            onNavigate={navigateToQuestion}
            disabled={faceDetectionEnabled && !faceDetected}
          />
        </div>

        {/* Question display */}
        <div className="md:col-span-3">
          <QuestionDisplay
            currentQuestion={questions[currentQuestionIndex]}
            currentQuestionIndex={currentQuestionIndex}
            totalQuestions={questions.length}
            answer={answers[questions[currentQuestionIndex]?._id]}
            onAnswerChange={handleAnswerChange}
            onNavigate={navigateToQuestion}
            onSubmit={() => {
              handleSubmitExam(false, "manual_confirm");
            }}
            faceDetectionEnabled={faceDetectionEnabled}
            faceDetected={faceDetected}
            submitLoading={submitLoading}
            answeredQuestions={Object.keys(answers)}
          />
        </div>
      </div>

      {/* Dialogs */}
      <WarningDialog
        open={showWarning}
        onAcknowledge={() => setShowWarning(false)}
        warningMessage={warningMessage}
        warningCount={warningCount}
        warningsAllowed={exam?.warningsAllowed}
      />

      <SubmissionWarningDialog
        open={showSubmissionWarning}
        onConfirm={() => {
          setShowSubmissionWarning(false);
          handleSubmitExam(true, "manual_confirm");
        }}
        onCancel={() => setShowSubmissionWarning(false)}
        unansweredQuestions={unansweredQuestions}
      />
    </div>
  );
};

export default ExamSession;